import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Link2, RefreshCw, Users, Phone, Mail, DollarSign, Calendar,
  MessageSquare, Tag, FileText, Download, Upload, CheckCircle,
  XCircle, Loader2, Search, ChevronLeft, ChevronRight, Clock,
  Zap, Database, Filter, BarChart3, ArrowDownToLine, Settings, AlertCircle
} from 'lucide-react';
import useArrowNav from '../hooks/useArrowNav';
import DraggableTabBar from '../components/DraggableTabBar';
import '../styles/GoHighLevelPage.css';
import PrintButton from '../components/PrintButton';

function GoHighLevelPage({ user }) {
  const [activeTab, setActiveTab] = useState('connection');
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const tabsRef = useRef(null);
  const { onKeyDown: handleTabKeyDown } = useArrowNav(tabsRef, 'button', { vertical: false, activateOnFocus: true });
  const [syncStatus, setSyncStatus] = useState(null);

  // Data states
  const [contacts, setContacts] = useState({ data: [], total: 0 });
  const [contactPage, setContactPage] = useState(1);
  const [contactSearch, setContactSearch] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState(null);
  const [opportunities, setOpportunities] = useState({ data: [], total: 0 });
  const [oppPage, setOppPage] = useState(1);
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState('');
  const [calendars, setCalendars] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState('');
  const [conversations, setConversations] = useState([]);
  const [invoices, setInvoices] = useState({ data: [], total: 0 });
  const [ghlUsers, setGhlUsers] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [tags, setTags] = useState([]);

  // Export/Import
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importingUsers, setImportingUsers] = useState(false);
  const [userImportResult, setUserImportResult] = useState(null);

  // Loading states
  const [loading, setLoading] = useState({});

  /*
   * ⛔⛔ C2 (P115) — DATA / EMPTY / FAILED ARE THREE STATES, NOT TWO.
   *
   * Every loader on this page was `try { if (data && !data.error) setX(...) }
   * catch { console.error(...) }`. On any failure — a thrown error OR a
   * `{ error }` payload — the state was simply left alone, so the list kept
   * its previous value (usually []) and the screen rendered the SAME "No
   * records" a genuinely empty GoHighLevel account renders. A staff member
   * could not tell "GHL has nothing for you" from "we could not reach GHL",
   * and the only trace was a console line nobody is watching.
   *
   * `status[key]` is therefore one of: 'idle' | 'loading' | 'ok' | 'empty' |
   * 'failed', and `errors[key]` carries the message for 'failed'.
   */
  const [status, setStatus] = useState({});
  const [errors, setErrors] = useState({});

  // Calendar window — a real control, replacing the hard-coded current month.
  // Defaults to the month so the first paint matches what staff saw before.
  const monthStart = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); };
  const monthEnd = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth() + 1, 0); };
  const toInputDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [calStart, setCalStart] = useState(() => toInputDate(monthStart()));
  const [calEnd, setCalEnd] = useState(() => toInputDate(monthEnd()));

  /**
   * Record the outcome of one loader. `count` decides ok vs empty, so an
   * EMPTY SUCCESS is recorded as empty rather than silently indistinguishable
   * from a failure that left the list at [].
   */
  const setOk = (key, count) => {
    setStatus((p) => ({ ...p, [key]: count > 0 ? 'ok' : 'empty' }));
    setErrors((p) => { const n = { ...p }; delete n[key]; return n; });
  };
  const setFailed = (key, err) => {
    setStatus((p) => ({ ...p, [key]: 'failed' }));
    setErrors((p) => ({ ...p, [key]: (err && err.message) || String(err) || 'Request failed' }));
  };

  useEffect(() => {
    loadSyncStatus();
    testConnection(true);
  }, []);

  async function testConnection(silent = false) {
    if (!silent) setConnecting(true);
    setConnectionError('');
    try {
      const result = await window.zignagenixAPI.ghlTestConnection();
      if (result?.success) {
        setConnected(true);
      } else {
        setConnected(false);
        if (!silent) setConnectionError(result?.error || 'Connection failed. Check API key and Location ID.');
      }
    } catch (err) {
      setConnected(false);
      if (!silent) setConnectionError(err.message || 'Connection test failed');
    } finally {
      setConnecting(false);
    }
  }

  async function loadSyncStatus() {
    try {
      const status = await window.zignagenixAPI.ghlGetSyncStatus();
      if (status && !status.error) setSyncStatus(status);
    } catch (err) { /* ignore */ }
  }

  const setLoadingKey = (key, val) => setLoading(prev => ({ ...prev, [key]: val }));

  /**
   * ⛔⛔ C2 — ONE renderer for the three outcomes, used by every list on this
   * page. A per-list `if` is how the page got here: eight loaders, eight
   * chances to collapse 'failed' into 'empty', and eight places for the next
   * one to be forgotten.
   *
   * Returns null when there is data to draw, so the caller renders its table.
   */
  function LoadState({ k, colSpan, emptyText }) {
    const st = status[k];
    if (st === 'loading') {
      return <tr><td colSpan={colSpan} className="ghl-empty"><Loader2 size={16} className="spin" /> Loading…</td></tr>;
    }
    if (st === 'failed') {
      return (
        <tr>
          <td colSpan={colSpan} className="ghl-empty" role="alert" data-testid={`ghl-failed-${k}`}>
            <AlertCircle size={16} /> Could not load from GoHighLevel — {errors[k] || 'request failed'}.
            {' '}This is NOT an empty result.
          </td>
        </tr>
      );
    }
    if (st === 'empty') {
      return <tr><td colSpan={colSpan} className="ghl-empty" data-testid={`ghl-empty-${k}`}>{emptyText}</td></tr>;
    }
    if (st === undefined || st === 'idle') {
      return <tr><td colSpan={colSpan} className="ghl-empty">Not loaded yet.</td></tr>;
    }
    return null;
  }

  // ── Data loaders ───────────────────────────────────────────────────
  async function loadContacts(page = 1) {
    setLoadingKey('contacts', true); setStatus((p) => ({ ...p, contacts: 'loading' }));
    try {
      const data = await window.zignagenixAPI.ghlGetContacts(page, 20);
      if (!data || data.error) throw new Error(data?.error || 'GoHighLevel returned no response');
      const rows = Array.isArray(data.contacts) ? data.contacts : [];
      setContacts({ data: rows, total: data.meta?.total || 0 });
      setContactPage(page);
      setOk('contacts', rows.length);
    } catch (err) { console.error('GHL contacts:', err); setFailed('contacts', err); }
    finally { setLoadingKey('contacts', false); }
  }

  async function searchContacts() {
    if (!contactSearch.trim()) { setContactSearchResults(null); return; }
    setLoadingKey('contactSearch', true);
    try {
      const data = await window.zignagenixAPI.ghlSearchContacts(contactSearch.trim());
      setContactSearchResults(Array.isArray(data?.contacts) ? data.contacts : []);
    } catch (err) { console.error('GHL search:', err); }
    finally { setLoadingKey('contactSearch', false); }
  }

  async function loadOpportunities(pipelineId, page = 1) {
    setLoadingKey('opportunities', true); setStatus((p) => ({ ...p, opportunities: 'loading' }));
    try {
      const data = await window.zignagenixAPI.ghlGetOpportunities(pipelineId, page, 20);
      if (!data || data.error) throw new Error(data?.error || 'GoHighLevel returned no response');
      const rows = Array.isArray(data.opportunities) ? data.opportunities : [];
      setOpportunities({ data: rows, total: data.meta?.total || 0 });
      setOppPage(page);
      setOk('opportunities', rows.length);
    } catch (err) { console.error('GHL opportunities:', err); setFailed('opportunities', err); }
    finally { setLoadingKey('opportunities', false); }
  }

  async function loadPipelines() {
    setLoadingKey('pipelines', true); setStatus((p) => ({ ...p, pipelines: 'loading' }));
    try {
      const data = await window.zignagenixAPI.ghlGetPipelines();
      if (!data || data.error) throw new Error(data?.error || 'GoHighLevel returned no response');
      const rows = Array.isArray(data.pipelines) ? data.pipelines : [];
      setPipelines(rows);
      setOk('pipelines', rows.length);
    } catch (err) { console.error('GHL pipelines:', err); setFailed('pipelines', err); }
    finally { setLoadingKey('pipelines', false); }
  }

  async function loadCalendars() {
    setLoadingKey('calendars', true); setStatus((p) => ({ ...p, calendars: 'loading' }));
    try {
      const data = await window.zignagenixAPI.ghlGetCalendars();
      if (!data || data.error) throw new Error(data?.error || 'GoHighLevel returned no response');
      const rows = Array.isArray(data.calendars) ? data.calendars : [];
      setCalendars(rows);
      setOk('calendars', rows.length);
    } catch (err) { console.error('GHL calendars:', err); setFailed('calendars', err); }
    finally { setLoadingKey('calendars', false); }
  }

  async function loadCalendarEvents(calId, startStr = calStart, endStr = calEnd) {
    setLoadingKey('calendarEvents', true);
    setStatus((p) => ({ ...p, calendarEvents: 'loading' }));
    try {
      /*
       * ⛔ C1b (P115): this window was HARD-CODED to the current month, so a
       * consult next month or last month's no-shows were simply invisible and
       * the heading said "Current Month" as if that were a choice. It is now
       * driven by the two date inputs in the toolbar.
       *
       * ⚠ EPOCH MILLISECONDS, which is what GHL wants. The end date is pushed
       * to the END of its day — a bare `new Date('2026-11-08')` is midnight
       * UTC, so an inclusive-looking range would have silently excluded
       * everything on its last day.
       */
      const start = new Date(`${startStr}T00:00:00`).getTime();
      const end = new Date(`${endStr}T23:59:59.999`).getTime();
      if (!Number.isFinite(start) || !Number.isFinite(end)) throw new Error('Invalid date range');
      if (end < start) throw new Error('End date is before start date');
      const data = await window.zignagenixAPI.ghlGetCalendarEvents(calId, start, end);
      if (!data || data.error) throw new Error(data?.error || 'GoHighLevel returned no response');
      const rows = Array.isArray(data.events) ? data.events : [];
      setCalendarEvents(rows);
      setOk('calendarEvents', rows.length);
    } catch (err) { console.error('GHL calendar events:', err); setCalendarEvents([]); setFailed('calendarEvents', err); }
    finally { setLoadingKey('calendarEvents', false); }
  }

  async function loadConversations() {
    setLoadingKey('conversations', true); setStatus((p) => ({ ...p, conversations: 'loading' }));
    try {
      const data = await window.zignagenixAPI.ghlGetConversations();
      if (!data || data.error) throw new Error(data?.error || 'GoHighLevel returned no response');
      const rows = Array.isArray(data.conversations) ? data.conversations : [];
      setConversations(rows);
      setOk('conversations', rows.length);
    } catch (err) { console.error('GHL conversations:', err); setFailed('conversations', err); }
    finally { setLoadingKey('conversations', false); }
  }

  async function loadInvoices(page = 1) {
    setLoadingKey('invoices', true); setStatus((p) => ({ ...p, invoices: 'loading' }));
    try {
      const data = await window.zignagenixAPI.ghlGetInvoices(page, 20);
      if (!data || data.error) throw new Error(data?.error || 'GoHighLevel returned no response');
      const rows = Array.isArray(data.invoices) ? data.invoices : [];
      setInvoices({ data: rows, total: data.meta?.total || 0 });
      setOk('invoices', rows.length);
    } catch (err) { console.error('GHL invoices:', err); setFailed('invoices', err); }
    finally { setLoadingKey('invoices', false); }
  }

  async function loadUsers() {
    setLoadingKey('users', true);
    try {
      const data = await window.zignagenixAPI.ghlGetUsers();
      if (data && !data.error) setGhlUsers(Array.isArray(data.users) ? data.users : []);
    } catch (err) { console.error('GHL users:', err); }
    finally { setLoadingKey('users', false); }
  }

  async function handleImportUsers() {
    if (!window.confirm(`Import ${ghlUsers.length} GHL user(s) into ZignaGenix?\n\nDuplicates (matching email) will be skipped.\nNew users will get a temporary password and must change it on first login.`)) return;
    setImportingUsers(true);
    setUserImportResult(null);
    try {
      const result = await window.zignagenixAPI.ghlImportUsers();
      if (result && result.error) {
        setUserImportResult({ error: true, message: result.message });
      } else {
        setUserImportResult(result);
        if (result.created > 0) loadUsers();
      }
    } catch (err) {
      setUserImportResult({ error: true, message: err.message });
    } finally {
      setImportingUsers(false);
    }
  }

  async function loadCustomFields() {
    setLoadingKey('customFields', true);
    try {
      const data = await window.zignagenixAPI.ghlGetCustomFields();
      if (data && !data.error) setCustomFields(Array.isArray(data.customFields) ? data.customFields : []);
    } catch (err) { console.error('GHL custom fields:', err); }
    finally { setLoadingKey('customFields', false); }
  }

  async function loadTags() {
    setLoadingKey('tags', true);
    try {
      const data = await window.zignagenixAPI.ghlGetTags();
      if (data && !data.error) setTags(Array.isArray(data.tags) ? data.tags : []);
    } catch (err) { console.error('GHL tags:', err); }
    finally { setLoadingKey('tags', false); }
  }

  async function handleExportAll() {
    setExporting(true);
    setExportResult(null);
    // Send heartbeats every 60s during export to prevent HIPAA session timeout
    const hbInterval = setInterval(async () => {
      try { await window.zignagenixAPI.sessionHeartbeat(); } catch (_) {}
    }, 60000);
    try {
      const result = await window.zignagenixAPI.ghlExportAll();
      setExportResult(result);
      if (result?.success) {
        await window.zignagenixAPI.ghlSaveSyncStatus({
          lastFullExport: new Date().toISOString(),
          contactCount: result.data?.contacts?.length || 0,
          opportunityCount: result.data?.opportunities?.length || 0,
        });
        loadSyncStatus();
      }
    } catch (err) {
      setExportResult({ error: err.message });
    } finally {
      clearInterval(hbInterval);
      setExporting(false);
    }
  }

  async function handleImport() {
    if (!exportResult?.data) return;
    setImporting(true);
    setImportResult(null);
    // Continuous heartbeat every 60s to cover long-running import phases
    const hbInterval = setInterval(async () => {
      try { await window.zignagenixAPI.sessionHeartbeat(); } catch (_) {}
    }, 60000);
    try {
      const data = exportResult.data;
      const contacts = data?.contacts || [];
      const summary = { contacts: { imported: 0, skipped: 0, skippedLocalEdits: 0, failed: 0 }, conversations: { imported: 0, skipped: 0, failed: 0, messages: 0 }, pipelines: { imported: 0, skipped: 0, failed: 0 }, tags: { imported: 0, skipped: 0, linked: 0, failed: 0 }, customFields: { imported: 0, skipped: 0, failed: 0 }, opportunities: { imported: 0, skipped: 0, failed: 0 } };
      const allErrors = [];

      // ── Phase 1: Import Contacts (batched) ──────────────────────────────
      if (contacts.length) {
        const BATCH_SIZE = 200;
        for (let i = 0; i < contacts.length; i += BATCH_SIZE) {
          const batch = contacts.slice(i, i + BATCH_SIZE);
          try {
            const result = await window.zignagenixAPI.ghlImportToCrm(batch);
            if (result?.success) {
              summary.contacts.imported += result.imported || 0;
              summary.contacts.skipped += result.skipped || 0;
              summary.contacts.skippedLocalEdits += result.skippedLocalEdits || 0;
              summary.contacts.failed += result.failed || 0;
              if (result.errors) allErrors.push(...result.errors);
            } else if (result?.error) {
              summary.contacts.failed += batch.length;
              allErrors.push({ phase: 'contacts', error: result.error });
            }
          } catch (batchErr) {
            summary.contacts.failed += batch.length;
            allErrors.push({ phase: 'contacts', error: batchErr.message });
          }
          try { await window.zignagenixAPI.sessionHeartbeat(); } catch (_) {}
          setImportResult({
            success: true, inProgress: true,
            phase: 'Contacts',
            progress: Math.min(i + BATCH_SIZE, contacts.length),
            total: contacts.length,
            summary,
          });
        }
      }

      // ── Phase 2: Import Pipelines & Stages ──────────────────────────────
      if (data.pipelines?.length) {
        setImportResult({ success: true, inProgress: true, phase: 'Pipelines', summary });
        try {
          const result = await window.zignagenixAPI.ghlImportPipelines(data.pipelines);
          if (result?.success) {
            summary.pipelines.imported = result.imported || 0;
            summary.pipelines.skipped = result.skipped || 0;
            summary.pipelines.failed = result.failed || 0;
          }
        } catch (e) { allErrors.push({ phase: 'pipelines', error: e.message }); }
        try { await window.zignagenixAPI.sessionHeartbeat(); } catch (_) {}
      }

      // ── Phase 3: Import Tags & link to contacts ─────────────────────────
      if (data.tags?.length) {
        setImportResult({ success: true, inProgress: true, phase: 'Tags', summary });
        try {
          const result = await window.zignagenixAPI.ghlImportTags({ tags: data.tags, contacts: contacts });
          if (result?.success) {
            summary.tags.imported = result.imported || 0;
            summary.tags.skipped = result.skipped || 0;
            summary.tags.linked = result.linked || 0;
            summary.tags.failed = result.failed || 0;
          }
        } catch (e) { allErrors.push({ phase: 'tags', error: e.message }); }
        try { await window.zignagenixAPI.sessionHeartbeat(); } catch (_) {}
      }

      // ── Phase 4: Import Custom Fields ───────────────────────────────────
      if (data.customFields?.length) {
        setImportResult({ success: true, inProgress: true, phase: 'Custom Fields', summary });
        try {
          const result = await window.zignagenixAPI.ghlImportCustomFields(data.customFields);
          if (result?.success) {
            summary.customFields.imported = result.imported || 0;
            summary.customFields.skipped = result.skipped || 0;
            summary.customFields.failed = result.failed || 0;
          }
        } catch (e) { allErrors.push({ phase: 'customFields', error: e.message }); }
        try { await window.zignagenixAPI.sessionHeartbeat(); } catch (_) {}
      }

      // ── Phase 5: Import Conversations → Interactions ────────────────────
      if (data.conversations?.length) {
        setImportResult({ success: true, inProgress: true, phase: 'Conversations', summary });
        try {
          const result = await window.zignagenixAPI.ghlImportConversations(data.conversations);
          if (result?.success) {
            summary.conversations.imported = result.imported || 0;
            summary.conversations.skipped = result.skipped || 0;
            summary.conversations.failed = result.failed || 0;
            summary.conversations.messages = result.messagesImported || 0;
          }
        } catch (e) { allErrors.push({ phase: 'conversations', error: e.message }); }
        try { await window.zignagenixAPI.sessionHeartbeat(); } catch (_) {}
      }

      // ── Phase 6: Import Opportunities → Pipeline stages ─────────────────
      if (data.opportunities?.length) {
        setImportResult({ success: true, inProgress: true, phase: 'Opportunities', summary });
        try {
          const result = await window.zignagenixAPI.ghlImportOpportunities({
            opportunities: data.opportunities,
            pipelines: data.pipelines || [],
          });
          if (result?.success) {
            summary.opportunities.imported = result.imported || 0;
            summary.opportunities.skipped = result.skipped || 0;
            summary.opportunities.failed = result.failed || 0;
          }
        } catch (e) { allErrors.push({ phase: 'opportunities', error: e.message }); }
        try { await window.zignagenixAPI.sessionHeartbeat(); } catch (_) {}
      }

      // ── Final result ────────────────────────────────────────────────────
      setImportResult({
        success: true,
        summary,
        errors: allErrors.slice(0, 30),
      });

      await window.zignagenixAPI.ghlSaveSyncStatus({
        lastImport: new Date().toISOString(),
        importedContacts: summary.contacts.imported,
        importedConversations: summary.conversations.imported,
        importedMessages: summary.conversations.messages,
        importedPipelines: summary.pipelines.imported,
        importedTags: summary.tags.imported,
        importedCustomFields: summary.customFields.imported,
        importedOpportunities: summary.opportunities.imported,
        skippedDuplicates: summary.contacts.skipped,
      });
      loadSyncStatus();
    } catch (err) {
      setImportResult({ error: err.message });
    } finally {
      clearInterval(hbInterval);
      setImporting(false);
    }
  }

  // Auto-load data when tab changes
  useEffect(() => {
    if (!connected) return;
    switch (activeTab) {
      case 'contacts': loadContacts(1); break;
      case 'opportunities': loadPipelines(); break;
      case 'calendars': loadCalendars(); break;
      case 'conversations': loadConversations(); break;
      case 'invoices': loadInvoices(1); break;
      case 'users': loadUsers(); break;
      case 'fields': loadCustomFields(); loadTags(); break;
    }
  }, [activeTab, connected]);

  const tabs = [
    { id: 'connection', label: 'Connection', icon: Link2 },
    { id: 'contacts', label: 'Contacts', icon: Users },
    { id: 'opportunities', label: 'Opportunities', icon: DollarSign },
    { id: 'calendars', label: 'Calendars', icon: Calendar },
    { id: 'conversations', label: 'Conversations', icon: MessageSquare },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'fields', label: 'Fields & Tags', icon: Tag },
    { id: 'sync', label: 'Sync & Export', icon: RefreshCw },
  ];

  return (
    <div className="page ghl-page">
      <div className="ghl-page-header">
        <div>
          <h1 className="page-title">GoHighLevel Integration</h1>
          <p className="page-subtitle">CRM data sync — pull contacts, opportunities, conversations and more</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <PrintButton pageTitle="GoHighLevel Integration" subtitle="CRM Data Sync" />
          <div className="ghl-connection-badge">
            {connected ? (
              <span className="ghl-badge ghl-badge--connected"><CheckCircle size={14} /> Connected</span>
            ) : (
              <span className="ghl-badge ghl-badge--disconnected"><XCircle size={14} /> Disconnected</span>
            )}
          </div>
        </div>
      </div>

      <DraggableTabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        prefKey="ghl_tab_order"
        className="ghl-tabs"
        tabClassName="ghl-tab"
        activeClassName="ghl-tab--active"
      />

      <div className="ghl-content">
        {activeTab === 'connection' && (
          <div className="ghl-section">
            <div className="ghl-card">
              <h3 className="ghl-card-title"><Settings size={18} /> Connection Settings</h3>
              <p className="ghl-card-desc">
                Configure your GoHighLevel API credentials in the <code>.env</code> file:
              </p>
              <div className="ghl-config-box">
                <div className="ghl-config-item">
                  <span className="ghl-config-key">GHL_API_KEY</span>
                  <span className="ghl-config-val">{process.env.GHL_API_KEY ? '••••••••' + (process.env.GHL_API_KEY || '').slice(-8) : 'Not set'}</span>
                </div>
                <div className="ghl-config-item">
                  <span className="ghl-config-key">GHL_LOCATION_ID</span>
                  <span className="ghl-config-val">{process.env.GHL_LOCATION_ID || 'Not set'}</span>
                </div>
              </div>
              <button
                className="btn-primary ghl-test-btn"
                onClick={() => testConnection(false)}
                disabled={connecting}
              >
                {connecting ? <><Loader2 size={16} className="spin" /> Testing...</> : <><Zap size={16} /> Test Connection</>}
              </button>
              {connectionError && <div className="ghl-error">{connectionError}</div>}
              {connected && <div className="ghl-success"><CheckCircle size={16} /> Successfully connected to GoHighLevel API</div>}
            </div>

            <div className="ghl-card">
              <h3 className="ghl-card-title"><Database size={18} /> Integration Overview</h3>
              <div className="ghl-overview-grid">
                <div className="ghl-overview-item">
                  <Users size={20} />
                  <span>Contacts</span>
                  <small>Pull all contacts with phones, emails, tags</small>
                </div>
                <div className="ghl-overview-item">
                  <DollarSign size={20} />
                  <span>Opportunities</span>
                  <small>Pipeline stages, values, status tracking</small>
                </div>
                <div className="ghl-overview-item">
                  <Calendar size={20} />
                  <span>Calendars</span>
                  <small>Appointments and scheduled events</small>
                </div>
                <div className="ghl-overview-item">
                  <MessageSquare size={20} />
                  <span>Conversations</span>
                  <small>SMS, email, and chat threads</small>
                </div>
                <div className="ghl-overview-item">
                  <FileText size={20} />
                  <span>Invoices</span>
                  <small>Billing and payment records</small>
                </div>
                <div className="ghl-overview-item">
                  <Tag size={20} />
                  <span>Fields & Tags</span>
                  <small>Custom fields and tag taxonomy</small>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="ghl-section">
            <div className="ghl-toolbar">
              <div className="ghl-search-bar">
                <Search size={16} />
                <input
                  placeholder="Search contacts by name, email, or phone..."
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchContacts()}
                />
                <button className="btn-sm btn-primary" onClick={searchContacts} disabled={loading.contactSearch}>
                  {loading.contactSearch ? <Loader2 size={14} className="spin" /> : 'Search'}
                </button>
                {contactSearchResults && (
                  <button className="btn-sm btn-ghost" onClick={() => { setContactSearchResults(null); setContactSearch(''); }}>
                    Clear
                  </button>
                )}
              </div>
              <button className="btn-sm btn-ghost" onClick={() => loadContacts(contactPage)} disabled={loading.contacts}>
                <RefreshCw size={14} className={loading.contacts ? 'spin' : ''} /> Refresh
              </button>
            </div>

            {contactSearchResults ? (
              <div className="ghl-data-section">
                <h3>Search Results ({contactSearchResults.length})</h3>
                <div className="ghl-table-wrap">
                  <table className="ghl-table">
                    <thead>
                      <tr><th>Name</th><th>Email</th><th>Phone</th><th>Tags</th><th>Source</th></tr>
                    </thead>
                    <tbody>
                      {contactSearchResults.map(c => (
                        <tr key={c.id}>
                          <td className="ghl-td-name">{c.firstNameRaw || c.firstName || ''} {c.lastNameRaw || c.lastName || ''}</td>
                          <td>{c.email || '—'}</td>
                          <td>{c.phone || '—'}</td>
                          <td>{(c.tags || []).join(', ') || '—'}</td>
                          <td>{c.source || '—'}</td>
                        </tr>
                      ))}
                      {contactSearchResults.length === 0 && <tr><td colSpan="5" className="ghl-empty">No contacts found</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="ghl-data-section">
                <h3>All Contacts {contacts.total > 0 && <span className="ghl-count">({contacts.total} total)</span>}</h3>
                {loading.contacts ? (
                  <div className="ghl-loading"><Loader2 size={24} className="spin" /> Loading contacts...</div>
                ) : (
                  <>
                    <div className="ghl-table-wrap">
                      <table className="ghl-table">
                        <thead>
                          <tr><th>Name</th><th>Email</th><th>Phone</th><th>Tags</th><th>Date Added</th></tr>
                        </thead>
                        <tbody>
                          {contacts.data.map(c => (
                            <tr key={c.id}>
                              <td className="ghl-td-name">{c.firstNameRaw || c.firstName || ''} {c.lastNameRaw || c.lastName || ''}</td>
                              <td>{c.email || '—'}</td>
                              <td>{c.phone || '—'}</td>
                              <td>{(c.tags || []).join(', ') || '—'}</td>
                              <td>{c.dateAdded ? new Date(c.dateAdded).toLocaleDateString() : '—'}</td>
                            </tr>
                          ))}
                          {contacts.data.length === 0 && <LoadState k="contacts" colSpan="5" emptyText="No contacts found in GoHighLevel." />}
                        </tbody>
                      </table>
                    </div>
                    <div className="ghl-pagination">
                      <button disabled={contactPage <= 1} onClick={() => loadContacts(contactPage - 1)}>
                        <ChevronLeft size={16} /> Previous
                      </button>
                      <span>Page {contactPage}</span>
                      <button disabled={contacts.data.length < 20} onClick={() => loadContacts(contactPage + 1)}>
                        Next <ChevronRight size={16} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'opportunities' && (
          <div className="ghl-section">
            <div className="ghl-toolbar">
              <div className="ghl-pipeline-select">
                <label>Pipeline:</label>
                <select
                  value={selectedPipeline}
                  onChange={(e) => {
                    setSelectedPipeline(e.target.value);
                    if (e.target.value) loadOpportunities(e.target.value, 1);
                  }}
                >
                  <option value="">Select a pipeline</option>
                  {pipelines.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              {loading.pipelines && <Loader2 size={16} className="spin" />}
            </div>

            {selectedPipeline && (
              <div className="ghl-data-section">
                <h3>Opportunities {opportunities.total > 0 && <span className="ghl-count">({opportunities.total} total)</span>}</h3>
                {loading.opportunities ? (
                  <div className="ghl-loading"><Loader2 size={24} className="spin" /> Loading opportunities...</div>
                ) : (
                  <>
                    <div className="ghl-table-wrap">
                      <table className="ghl-table">
                        <thead>
                          <tr><th>Name</th><th>Contact</th><th>Stage</th><th>Value</th><th>Status</th><th>Updated</th></tr>
                        </thead>
                        <tbody>
                          {opportunities.data.map(o => (
                            <tr key={o.id}>
                              <td className="ghl-td-name">{o.name || '—'}</td>
                              <td>{o.contact?.name || o.contactId || '—'}</td>
                              <td><span className="ghl-stage-badge">{o.pipelineStageId || '—'}</span></td>
                              <td className="ghl-td-money">${(o.monetaryValue || 0).toLocaleString()}</td>
                              <td><span className={`ghl-status ghl-status--${(o.status || 'open').toLowerCase()}`}>{o.status || 'open'}</span></td>
                              <td>{o.updatedAt ? new Date(o.updatedAt).toLocaleDateString() : '—'}</td>
                            </tr>
                          ))}
                          {opportunities.data.length === 0 && <LoadState k="opportunities" colSpan="6" emptyText="No opportunities in this pipeline." />}
                        </tbody>
                      </table>
                    </div>
                    <div className="ghl-pagination">
                      <button disabled={oppPage <= 1} onClick={() => loadOpportunities(selectedPipeline, oppPage - 1)}>
                        <ChevronLeft size={16} /> Previous
                      </button>
                      <span>Page {oppPage}</span>
                      <button disabled={opportunities.data.length < 20} onClick={() => loadOpportunities(selectedPipeline, oppPage + 1)}>
                        Next <ChevronRight size={16} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'calendars' && (
          <div className="ghl-section">
            <div className="ghl-toolbar">
              <div className="ghl-pipeline-select">
                <label>Calendar:</label>
                <select
                  value={selectedCalendar}
                  onChange={(e) => {
                    setSelectedCalendar(e.target.value);
                    if (e.target.value) loadCalendarEvents(e.target.value);
                  }}
                >
                  <option value="">Select a calendar</option>
                  {calendars.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              {loading.calendars && <Loader2 size={16} className="spin" />}
            </div>

            {selectedCalendar && (
              <div className="ghl-data-section">
                {/* C1b: the window is a control now, not a hard-coded month. */}
                <div className="ghl-toolbar">
                  <label htmlFor="ghl-cal-start">From:</label>
                  <input id="ghl-cal-start" type="date" value={calStart}
                    aria-label="Events from date"
                    onChange={(e) => setCalStart(e.target.value)} />
                  <label htmlFor="ghl-cal-end">To:</label>
                  <input id="ghl-cal-end" type="date" value={calEnd}
                    aria-label="Events to date"
                    onChange={(e) => setCalEnd(e.target.value)} />
                  <button type="button" className="ghl-btn"
                    onClick={() => loadCalendarEvents(selectedCalendar, calStart, calEnd)}>
                    Apply
                  </button>
                </div>
                <h3>Events ({calStart} → {calEnd})</h3>
                {loading.calendarEvents ? (
                  <div className="ghl-loading"><Loader2 size={24} className="spin" /> Loading events...</div>
                ) : (
                  <div className="ghl-table-wrap">
                    <table className="ghl-table">
                      <thead>
                        <tr><th>Title</th><th>Contact</th><th>Start</th><th>End</th><th>Status</th></tr>
                      </thead>
                      <tbody>
                        {calendarEvents.map((e, i) => (
                          <tr key={e.id || i}>
                            <td className="ghl-td-name">{e.title || e.calendarId || '—'}</td>
                            <td>{e.contact?.name || e.contactId || '—'}</td>
                            <td>{e.startTime ? new Date(e.startTime).toLocaleString() : '—'}</td>
                            <td>{e.endTime ? new Date(e.endTime).toLocaleString() : '—'}</td>
                            <td><span className={`ghl-status ghl-status--${(e.status || 'confirmed').toLowerCase()}`}>{e.status || 'confirmed'}</span></td>
                          </tr>
                        ))}
                        {calendarEvents.length === 0 && <LoadState k="calendarEvents" colSpan="5" emptyText="No events in this date range." />}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'conversations' && (
          <div className="ghl-section">
            <div className="ghl-toolbar">
              <button className="btn-sm btn-ghost" onClick={loadConversations} disabled={loading.conversations}>
                <RefreshCw size={14} className={loading.conversations ? 'spin' : ''} /> Refresh
              </button>
            </div>
            <div className="ghl-data-section">
              <h3>Recent Conversations</h3>
              {loading.conversations ? (
                <div className="ghl-loading"><Loader2 size={24} className="spin" /> Loading conversations...</div>
              ) : (
                <div className="ghl-table-wrap">
                  <table className="ghl-table">
                    <thead>
                      <tr><th>Contact</th><th>Type</th><th>Last Message</th><th>Unread</th><th>Updated</th></tr>
                    </thead>
                    <tbody>
                      {conversations.map(c => (
                        <tr key={c.id}>
                          <td className="ghl-td-name">{c.contactName || c.contactId || '—'}</td>
                          <td>{c.type || '—'}</td>
                          <td className="ghl-td-preview">{c.lastMessageBody?.substring(0, 60) || '—'}</td>
                          <td>{c.unreadCount || 0}</td>
                          <td>{c.dateUpdated ? new Date(c.dateUpdated).toLocaleString() : '—'}</td>
                        </tr>
                      ))}
                      {conversations.length === 0 && <LoadState k="conversations" colSpan="5" emptyText="No conversations found." />}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="ghl-section">
            <div className="ghl-toolbar">
              <button className="btn-sm btn-ghost" onClick={() => loadInvoices(1)} disabled={loading.invoices}>
                <RefreshCw size={14} className={loading.invoices ? 'spin' : ''} /> Refresh
              </button>
            </div>
            <div className="ghl-data-section">
              <h3>Invoices</h3>
              {loading.invoices ? (
                <div className="ghl-loading"><Loader2 size={24} className="spin" /> Loading invoices...</div>
              ) : (
                <div className="ghl-table-wrap">
                  <table className="ghl-table">
                    <thead>
                      <tr><th>Invoice #</th><th>Contact</th><th>Amount</th><th>Status</th><th>Due Date</th></tr>
                    </thead>
                    <tbody>
                      {invoices.data.map(inv => (
                        <tr key={inv.id || inv._id}>
                          <td>{inv.invoiceNumber || inv._id || '—'}</td>
                          <td>{inv.contactName || inv.contactId || '—'}</td>
                          <td className="ghl-td-money">${(inv.total || inv.amount || 0).toLocaleString()}</td>
                          <td><span className={`ghl-status ghl-status--${(inv.status || 'draft').toLowerCase()}`}>{inv.status || 'draft'}</span></td>
                          <td>{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                      {invoices.data.length === 0 && <LoadState k="invoices" colSpan="5" emptyText="No invoices found." />}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="ghl-section">
            <div className="ghl-toolbar">
              <button className="btn-sm btn-ghost" onClick={loadUsers} disabled={loading.users}>
                <RefreshCw size={14} className={loading.users ? 'spin' : ''} /> Refresh
              </button>
              <button className="btn-sm btn-primary" onClick={handleImportUsers} disabled={importingUsers || ghlUsers.length === 0}>
                {importingUsers ? <><Loader2 size={14} className="spin" /> Importing...</> : <><Users size={14} /> Import Users to ZignaGenix</>}
              </button>
            </div>
            {userImportResult && (
              <div style={{ padding: '12px 16px', marginBottom: 12, borderRadius: 8, background: userImportResult.error ? '#FEF2F2' : '#F0FDF4', border: `1px solid ${userImportResult.error ? '#FECACA' : '#BBF7D0'}` }}>
                {userImportResult.error ? (
                  <span style={{ color: '#DC2626' }}>Error: {userImportResult.message}</span>
                ) : (
                  <div>
                    <strong style={{ color: '#16A34A' }}>Import Complete:</strong>{' '}
                    {userImportResult.created} created, {userImportResult.skipped} skipped (duplicates), {userImportResult.failed} failed
                    {userImportResult.details && userImportResult.details.filter(d => d.status === 'created').length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <strong>New accounts:</strong>
                        <div className="responsive-scroll">
                        <table style={{ width: '100%', marginTop: 4, fontSize: 12, borderCollapse: 'collapse', minWidth: 640 }}>
                          <thead><tr style={{ background: '#F5F6FA' }}>
                            <th style={{ padding: '4px 8px', textAlign: 'left' }}>Name</th>
                            <th style={{ padding: '4px 8px', textAlign: 'left' }}>Username</th>
                            <th style={{ padding: '4px 8px', textAlign: 'left' }}>Email</th>
                            <th style={{ padding: '4px 8px', textAlign: 'left' }}>Role</th>
                            <th style={{ padding: '4px 8px', textAlign: 'left' }}>Temp Password</th>
                          </tr></thead>
                          <tbody>
                            {userImportResult.details.filter(d => d.status === 'created').map((d, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #E8ECF1' }}>
                                <td style={{ padding: '4px 8px' }}>{d.name}</td>
                                <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{d.username}</td>
                                <td style={{ padding: '4px 8px' }}>{d.email}</td>
                                <td style={{ padding: '4px 8px' }}>{d.role}</td>
                                <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{d.tempPassword}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="ghl-data-section">
              <h3>GHL Users</h3>
              {loading.users ? (
                <div className="ghl-loading"><Loader2 size={24} className="spin" /> Loading users...</div>
              ) : (
                <div className="ghl-table-wrap">
                  <table className="ghl-table">
                    <thead>
                      <tr><th>Name</th><th>Email</th><th>Role</th><th>Phone</th></tr>
                    </thead>
                    <tbody>
                      {ghlUsers.map(u => (
                        <tr key={u.id}>
                          <td className="ghl-td-name">{u.name || u.firstName + ' ' + u.lastName || '—'}</td>
                          <td>{u.email || '—'}</td>
                          <td>{u.role || u.type || '—'}</td>
                          <td>{u.phone || '—'}</td>
                        </tr>
                      ))}
                      {ghlUsers.length === 0 && <tr><td colSpan="4" className="ghl-empty">No users found</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'fields' && (
          <div className="ghl-section">
            <div className="ghl-fields-grid">
              <div className="ghl-data-section">
                <h3>Custom Fields <span className="ghl-count">({customFields.length})</span></h3>
                {loading.customFields ? (
                  <div className="ghl-loading"><Loader2 size={24} className="spin" /> Loading...</div>
                ) : (
                  <div className="ghl-table-wrap ghl-table-wrap--compact">
                    <table className="ghl-table">
                      <thead><tr><th>Name</th><th>Key</th><th>Type</th></tr></thead>
                      <tbody>
                        {customFields.map(f => (
                          <tr key={f.id}>
                            <td>{f.name || '—'}</td>
                            <td><code>{f.fieldKey || f.key || '—'}</code></td>
                            <td>{f.dataType || f.type || '—'}</td>
                          </tr>
                        ))}
                        {customFields.length === 0 && <tr><td colSpan="3" className="ghl-empty">No custom fields</td></tr>}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="ghl-data-section">
                <h3>Tags <span className="ghl-count">({tags.length})</span></h3>
                {loading.tags ? (
                  <div className="ghl-loading"><Loader2 size={24} className="spin" /> Loading...</div>
                ) : (
                  <div className="ghl-tags-list">
                    {tags.map((t, i) => (
                      <span key={i} className="ghl-tag-chip">{typeof t === 'string' ? t : t.name || t.tag || '—'}</span>
                    ))}
                    {tags.length === 0 && <p className="ghl-empty">No tags found</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'sync' && (
          <div className="ghl-section">
            <div className="ghl-sync-grid">
              <div className="ghl-card">
                <h3 className="ghl-card-title"><Download size={18} /> Full Export from GHL</h3>
                <p className="ghl-card-desc">
                  Export all contacts (up to 5,000), opportunities, pipelines, calendars, conversations, invoices, users, custom fields, and tags in a single batch.
                </p>
                <button
                  className="btn-primary ghl-action-btn"
                  onClick={handleExportAll}
                  disabled={exporting || !connected}
                >
                  {exporting ? <><Loader2 size={16} className="spin" /> Exporting...</> : <><ArrowDownToLine size={16} /> Export All Data</>}
                </button>
                {exportResult?.success && (
                  <div className="ghl-success">
                    <CheckCircle size={16} /> Export complete — {exportResult.data?.contacts?.length || 0} contacts,{' '}
                    {exportResult.data?.opportunities?.length || 0} opportunities,{' '}
                    {exportResult.data?.pipelines?.length || 0} pipelines
                  </div>
                )}
                {exportResult?.error && <div className="ghl-error">{exportResult.error}</div>}
              </div>

              <div className="ghl-card">
                <h3 className="ghl-card-title"><Upload size={18} /> Import to ZignaGenix CRM</h3>
                <p className="ghl-card-desc">
                  Import exported GHL contacts into ZignaGenix CRM. Duplicates are detected by email and phone and automatically skipped.
                </p>
                <button
                  className="btn-primary ghl-action-btn"
                  onClick={handleImport}
                  disabled={importing || !exportResult?.data}
                >
                  {importing ? <><Loader2 size={16} className="spin" /> Importing...</> : <><Upload size={16} /> Import to CRM</>}
                </button>
                {!exportResult?.data && <p className="ghl-hint">Run a full export first before importing.</p>}
                {importResult?.success && importResult.inProgress && (
                  <div className="ghl-success">
                    <Loader2 size={16} className="spin" />
                    {` Importing ${importResult.phase || ''}... `}
                    {importResult.progress != null && `${importResult.progress} / ${importResult.total} processed`}
                  </div>
                )}
                {importResult?.success && !importResult.inProgress && importResult.summary && (
                  <div className="ghl-success" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                    <div><CheckCircle size={16} style={{ verticalAlign: 'middle' }} /> <strong>Import complete</strong></div>
                    <div style={{ fontSize: 12, lineHeight: '1.5', paddingLeft: 22 }}>
                      {(importResult.summary.contacts.imported > 0 || importResult.summary.contacts.skippedLocalEdits > 0) && <div>Contacts: {importResult.summary.contacts.imported} imported, {importResult.summary.contacts.skipped} skipped{importResult.summary.contacts.skippedLocalEdits > 0 ? ` (${importResult.summary.contacts.skippedLocalEdits} skipped — locally edited, protected)` : ''}</div>}
                      {importResult.summary.pipelines.imported > 0 && <div>Pipelines: {importResult.summary.pipelines.imported} imported</div>}
                      {importResult.summary.tags.imported > 0 && <div>Tags: {importResult.summary.tags.imported} created, {importResult.summary.tags.linked} linked to contacts</div>}
                      {importResult.summary.customFields.imported > 0 && <div>Custom Fields: {importResult.summary.customFields.imported} imported</div>}
                      {importResult.summary.conversations.imported > 0 && <div>Conversations → Interactions: {importResult.summary.conversations.imported} threads, {importResult.summary.conversations.messages || 0} messages imported</div>}
                      {importResult.summary.opportunities.imported > 0 && <div>Opportunities → Pipeline stages: {importResult.summary.opportunities.imported} imported</div>}
                      {Object.values(importResult.summary).every(s => s.imported === 0) && !(importResult.summary.contacts.skippedLocalEdits > 0) && <div>All data already synced — nothing new to import.</div>}
                    </div>
                  </div>
                )}
                {importResult?.error && <div className="ghl-error">{importResult.error}</div>}
              </div>

              <div className="ghl-card ghl-card--full">
                <h3 className="ghl-card-title"><Clock size={18} /> Sync History</h3>
                {syncStatus ? (
                  <div className="ghl-sync-details">
                    {syncStatus.lastFullExport && (
                      <div className="ghl-sync-row">
                        <span className="ghl-sync-label">Last Full Export</span>
                        <span className="ghl-sync-value">{new Date(syncStatus.lastFullExport).toLocaleString()}</span>
                        <span className="ghl-sync-meta">{syncStatus.contactCount || 0} contacts, {syncStatus.opportunityCount || 0} opportunities</span>
                      </div>
                    )}
                    {syncStatus.lastImport && (
                      <div className="ghl-sync-row">
                        <span className="ghl-sync-label">Last Import</span>
                        <span className="ghl-sync-value">{new Date(syncStatus.lastImport).toLocaleString()}</span>
                        <span className="ghl-sync-meta">{syncStatus.importedContacts || 0} contacts, {syncStatus.importedConversations || 0} interactions, {syncStatus.importedPipelines || 0} pipelines, {syncStatus.importedTags || 0} tags, {syncStatus.importedOpportunities || 0} opportunities</span>
                      </div>
                    )}
                    {!syncStatus.lastFullExport && !syncStatus.lastImport && (
                      <p className="ghl-empty">No sync history yet. Run a full export to get started.</p>
                    )}
                  </div>
                ) : (
                  <p className="ghl-empty">No sync history yet.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {!connected && activeTab !== 'connection' && (
          <div className="ghl-disconnected-notice">
            <XCircle size={24} />
            <p>Not connected to GoHighLevel. Go to the <strong>Connection</strong> tab to configure your API credentials.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default GoHighLevelPage;
